
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    Paper,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Chip
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AddIcon from "@mui/icons-material/Add";
import {useTheme} from '@mui/material/styles';
import {ListViewModel,
        ListViewColumnModel,
        EmailRecipientModel,
        RecordModel} from '../../models/models';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {useSnackbar} from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {Link, useParams} from 'react-router-dom';
import {getAppName, getObjectName } from "../../utils/helperRouter";
import {validateEmailField} from '../../utils/fieldValidations';
import { searchRecipients } from "../../services/emailApi";
import { useAuth } from "../../hooks/useAuth";
import PeopleIcon from '@mui/icons-material/People';
import debounce from "lodash.debounce";

export const EmailToComponent: React.FC<{
  emailRecipients: EmailRecipientModel[],
  onAdd: (newRecipient: EmailRecipientModel) => void
  onRemove: (thisRecipient: EmailRecipientModel) => void
}> = ({
  emailRecipients,
  onAdd,
  onRemove
}) => {

  const [searchText, setSearchText] = useState<string>("");

  const [recipientOptions, setRecipientOptions] = useState<EmailRecipientModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>("");

  const {accessToken, onInvalidToken} = useAuth();
  let {appName, objectName, recordUuid} = useParams();

  const searchRecipientsDebounced = useMemo(
    () =>
    debounce(async (search: string) => {
      if(!search){
        setRecipientOptions([]);
        setIsLoading(false);
        return;
      }

      const thisSearchText = search.trim()
      const thisObjectApiName = getObjectName(objectName);

      searchRecipients(accessToken, thisObjectApiName, thisSearchText)
         .then((results) => {
           setRecipientOptions(results);
           setIsLoading(false);
           setErrorMessage("");
         })
         .catch((err) => {
           setErrorMessage(err.message)
           setIsLoading(false)
         })
    }, 1000),
    []
  );


  useEffect(() => {
    if(searchText.trim() !== "" &&
       searchText.trim().length >= 4){
      setIsLoading(true)
      searchRecipientsDebounced(searchText)
    } else {
      setRecipientOptions([])
    }

  }, [searchText, searchRecipientsDebounced])


  const handleAddRecipient = (value: EmailRecipientModel | string | null) => {
    if(!value) return;
    if(typeof value === "string"){
      const thisEmail = searchText.trim()
      const patternMatched = validateEmailField(thisEmail)
      if(!patternMatched){
        setErrorMessage("Invalid Email Address")
      } else {
        setErrorMessage("");
        setSearchText("");
        const newRecipient = new EmailRecipientModel(thisEmail)
        onAdd(newRecipient);
      }
    } else{
      setErrorMessage("");
      setSearchText("");
      onAdd(value)
    }
}

  const handleClickEvent = () => {
    handleAddRecipient(searchText)
  }

  const handleClear = () => {
    setSearchText("");
  }


/*
  const error2 = () => {
    if(emailRecipients.length < 1) return true;
    return false
  };
*/

  const getEmailRecipientLabel = (emailRecipient: EmailRecipientModel) => {
    if(emailRecipient.contextRecord) return `${emailRecipient.contextRecord.lookupDisplayValue} <${emailRecipient.email}>`
    return emailRecipient.email
  }

  return (
   <Box>

    <Box sx={{
          display: "flex",
          gap: "1rem",
          alignItems: 'center',
          pt: '1rem',
          pl: '1rem',
          pr: '1rem',
        }}
    >
     <Typography variant="subtitle1">
       To:
     </Typography>

     <Box  sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          alignItems: 'center'
        }}
        >

      {emailRecipients.map((emailRecipient: EmailRecipientModel)=> {
          return (
            <Chip
              sx={{
                pl: '0.5rem',
                pr: '0.5rem',
                gap: '0.5rem',
                alignItems: 'center',
                justifyContent: 'center',
                height: '2.5rem',
                borderRadius: 2
              }}
              key={emailRecipient.email}
              label={getEmailRecipientLabel(emailRecipient)}
              onDelete={() => onRemove(emailRecipient)}
              icon={emailRecipient.contextRecord ? <PeopleIcon/> : undefined}
            />
          )
        })}

    <Box sx={{
      display: "flex",
      flexDirection: "row",
      minWidth: "20rem"
    }}
    >


        <Autocomplete
          freeSolo
          disableClearable
          options={recipientOptions}
          fullWidth
          getOptionLabel={(option) =>{
            if(typeof option === "string") return option
            if(option && option.contextRecord) return `${option.contextRecord.lookupDisplayValue} <${option.email}>`
            return option.email
          }}
          filterOptions={(x) => x}
          onInputChange={(_, value, reason) => {
            if(reason !== 'reset') {
              setSearchText(value)
            }
          }}
          onChange={(_, value) => {
            handleAddRecipient(value)
          }}
          inputValue={searchText}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.email}
            >
              {getEmailRecipientLabel(option)}

            </li>

          )}

          renderInput={(params) => (
            <TextField
              {...params}
              error={errorMessage !== ""}
              required={true}
              size="small"
              label="To"
              variant="filled"
              helperText={errorMessage}
              placeholder="Type email or name"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >

                      {isLoading && <CircularProgress size={18}/>}
                      <InputAdornment position="end">
                        <Tooltip title="Add email">
                          <IconButton disabled={searchText === ""} size="small" onClick={() => handleClickEvent()}>
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>

                      <InputAdornment position="end">
                        <Tooltip title="Clear">
                          <IconButton disabled={searchText === ""} size="small" onClick={() => handleClear()}>
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>



                      {params.InputProps.endAdornment}
                    </Box>
                )
              }}
            />
          )
          }
        />
        </Box>
    </Box>

      </Box>
      </Box>

    )
};
